/** What the app shell needs to mount the router; off the main barrel because its signatures name TanStack types. */
// Only `App.tsx`, `AuthenticatedLayout.tsx` and app tests import this; routes use the barrel's router-agnostic hooks.
import type { RouterHistory } from '@tanstack/history';
import {
    type AnyContext,
    type AnyRoute,
    type AnyRouter,
    createRouter,
    type ErrorRouteComponent,
    type NotFoundRouteComponent,
    type RouteComponent,
    type Router,
    type RouterContextOptions,
    RouterProvider,
} from '@tanstack/react-router';
import * as React from 'react';
import type { JSX } from 'react';

import type { AppLinkOptions } from './links/linkOptions';
import { MountedRouterRegistrar } from './links/mountedRouter';
import { routeChainWith } from './links/useCanOpen';
import { type MovedPage, setMovedPages } from './movedPages';
import { parseSearch, stringifySearch } from './searchParams';
import type { RouteStaticData } from './useRouteMatches';

/** The root's context without `routeChainOf`, which {@link createAppRouter} adds. */
type CallerContext<TRouteTree extends AnyRoute> = Omit<TRouteTree['types']['routerContext'], 'routeChainOf'>;

/**
 * `context` is required exactly when the root route declares one with `createRootRouteWithContext` that holds more
 * than `routeChainOf`: TanStack's `RouterContextOptions` over {@link CallerContext}.
 */
type CreateAppRouterOptions<TRouteTree extends AnyRoute> = (AnyContext extends CallerContext<TRouteTree>
    ? { context?: CallerContext<TRouteTree> }
    : { context: CallerContext<TRouteTree> }) & {
    /** The generated route tree (`routeTree.gen.ts`). */
    routeTree: TRouteTree;
    /**
     * `router.history` once created: `'browser'` in `App.tsx`, a memory history (`memoryHistoryAt` from
     * `router-testing`) in a test. Required, so a test can't fall back to the browser history, which under jsdom
     * shares one `window.history` with every other test in the file.
     */
    history: RouterHistory | 'browser';
    defaultErrorComponent?: ErrorRouteComponent;
    defaultPendingComponent?: RouteComponent;
    /**
     * Replaces a route's component when its `beforeLoad` or `loader` throws `notFound()` and neither it nor a route
     * above it declares a `notFoundComponent`; also fills a layout's `<Outlet />` for a URL no route below it matches.
     */
    defaultNotFoundComponent?: NotFoundRouteComponent;
    /** Called by every route error boundary without its own `onCatch`; only routes with an error component have one. */
    defaultOnCatch?: (error: unknown, errorInfo: React.ErrorInfo) => void;
    /**
     * Scroll areas that start each new history entry at the top, such as a page's own scroll container (see
     * `scrollRestoration` below). Each selector resets the first element it matches.
     */
    scrollToTopSelectors?: string[];
    /**
     * Pages with a second route. Under its `to`, `useRouteName` names a page by its `from`, and `useIsRouteActive`
     * counts a link to its `from` as active, so a tab bar keyed by the routes links point at still finds the page.
     * `useNavigate` sends the page's own navigation to its `from` straight to its `to`, with every option it passed.
     * Page views and the Sentry `route` tag keep the route's own name.
     */
    movedPages?: ReadonlyArray<MovedPage>;
};

/**
 * The app's router. It sets only the options the app needs to differ; every other one is TanStack's default on
 * purpose, so the router behaves as TanStack's docs describe. `App.tsx` registers it (`declare module … Register`,
 * decided 2026-09-21), so the route files' `Route` hooks and the barrel's links, `navigate`, `getRouteApi`,
 * `useSearch` and `useSetSearch` are typed against the generated tree.
 * The return type is declared: inferring it checks the body, whose `AppLinkOptions` reads the registered router
 * while `App.tsx`'s `typeof router` is still being resolved, and the checker then caches broken link types.
 */
export function createAppRouter<TRouteTree extends AnyRoute>({
    routeTree,
    history,
    defaultErrorComponent,
    defaultPendingComponent,
    defaultNotFoundComponent,
    defaultOnCatch,
    scrollToTopSelectors,
    movedPages = [],
    ...rest
}: CreateAppRouterOptions<TRouteTree>): Router<TRouteTree> {
    // Only `context` is taken from `rest`: a spread past the excess-property check would override the fixed options.
    const { context } = rest as { context?: object };
    const router = createRouter({
        routeTree,
        ...(history === 'browser' ? {} : { history }),
        // The URLSearchParams codec keeps email links, bookmarks and outside producers working, where TanStack's
        // default JSON-coerces values and rewrites those URLs ("URLs keep opening the page they name" in
        // `documentation/routing-rationale.md`).
        parseSearch,
        stringifySearch,
        // On hover and focus a link loads its route's chunk and runs its `beforeLoad` and `loader`. Without it every
        // chunk loads after the click, with no pending UI for up to a second.
        defaultPreload: 'intent',
        // Leaving a history entry records where the window and each scrolled element were; Back or Forward puts them
        // back once the page has rendered. A new entry scrolls the window and `scrollToTopSelectors` to the top.
        // Every other element keeps its position, carried to the element in the same place on the next page (by
        // `data-scroll-restoration-id`, else DOM position): right for a layout's scroll area, such as a menu, wrong
        // for a page's own, so the app names those. A virtualized list starts from `useElementScrollRestoration`.
        // A query-string write (`useSetSearch`) moves nothing.
        scrollRestoration: true,
        ...(scrollToTopSelectors ? { scrollToTopSelectors } : {}),
        ...(defaultErrorComponent ? { defaultErrorComponent } : {}),
        ...(defaultPendingComponent ? { defaultPendingComponent } : {}),
        ...(defaultNotFoundComponent ? { defaultNotFoundComponent } : {}),
        ...(defaultOnCatch ? { defaultOnCatch } : {}),
        // Checked at the call site; TypeScript can't resolve `RouterContextOptions` through `TRouteTree` here. The
        // closure's types are spelled out, as `router` is still being declared.
        ...({
            context: {
                ...context,
                routeChainOf: (link: AppLinkOptions): ReadonlyArray<Partial<RouteStaticData>> =>
                    routeChainWith(router, link),
            },
        } as RouterContextOptions<TRouteTree>),
    });
    setMovedPages(router, movedPages);
    return router;
}

type AppRouterProviderProps<TRouter extends AnyRouter> = {
    router: TRouter;
};

/** Mounts a router made by {@link createAppRouter}. */
export function AppRouterProvider<TRouter extends AnyRouter>({ router }: AppRouterProviderProps<TRouter>): JSX.Element {
    // `createElement` rather than JSX: this file is `.ts` because the package's `exports` map names it.
    const provider = React.createElement(RouterProvider, { router } as React.ComponentProps<typeof RouterProvider>);
    // A sibling of the provider, so `buildHref` finds this router as soon as it mounts.
    const registrar = React.createElement(MountedRouterRegistrar, { router });
    return React.createElement(React.Fragment, null, registrar, provider);
}

// For the app shell's page views and Sentry `route` tag.
export { subscribeToPageViews } from './pageViews';
export { subscribeToRouteTag } from './routeName';
// The session's rule for which links show: mounted once by the app shell, so a page can't mount a rule of its own.
export { LinkAccessProvider } from './links/useCanOpen';
